import { Component, OnInit } from '@angular/core';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CausaApagado, EstadoIA, IaService, ModeloCatalogo } from 'src/app/services/ia/ia.service';
import {
  CAUSAS, errorDeIA, vacio, haceCuanto, nombreCausa, nombreProveedor, puedeConfigurarIA, usd,
} from 'src/app/services/ia/ia-formato';

/**
 * SOLAPA «ESTADO» — `console/ia`.
 *
 * Lo que decide quien abre esta pantalla: ¿la IA está corriendo, con qué clave,
 * con qué modelo y cuánto lleva gastado hoy? En ese orden:
 *
 *   1. el interruptor general (apagar pide escribir APAGAR; prender, nada);
 *   2. las claves por proveedor, con de dónde sale la que se usa;
 *   3. el modelo por omisión (del catálogo) y el tope diario, con el gasto de hoy.
 *
 * 🔴 LA ÚNICA EXCEPCIÓN A «NADA DE MODALES» ES LA CONFIRMACIÓN DE APAGAR
 * (CONTRATO §2 y §8.2). Un modal molesta porque interrumpe, y acá interrumpir es
 * justamente la función. Prender no pide nada: el riesgo es asimétrico.
 *
 * 📍 Copiado del piloto (`pasarela_consola/components/ia/interruptor-ia.tsx`),
 * con una diferencia: aquí sí hay catálogo de causas (§7.2), así que se manda.
 */
@Component({
  selector: 'app-ia-estado',
  templateUrl: './ia-estado.component.html',
  styleUrls: ['./ia-estado.component.css']
})
export class IaEstadoComponent implements OnInit {

  cargando = true;
  error: string | null = null;
  estado: EstadoIA | null = null;

  catalogo: ModeloCatalogo[] = [];
  errorCatalogo: string | null = null;

  // ── ajustes: modelo por omisión y tope diario ──
  modelo = '';
  tope: any = '';
  guardandoAjustes = false;
  resultadoAjustes: { ok: boolean; texto: string } | null = null;

  // ── el interruptor ──
  mostrarApagar = false;
  causa: CausaApagado = 'INCIDENTE';
  motivo = '';
  palabra = '';
  cambiandoApagado = false;
  errorApagado: string | null = null;

  readonly CAUSAS = CAUSAS;
  readonly usd = usd;
  readonly haceCuanto = haceCuanto;
  readonly nombreCausa = nombreCausa;
  readonly nombreProveedor = nombreProveedor;

  constructor(private _ia: IaService, public _auth: AuthService) { }

  ngOnInit(): void { this.cargar(); }

  get puedeEditar(): boolean { return puedeConfigurarIA(this._auth); }

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.error = null;
    const [est, cat] = await Promise.all([
      this._ia.getEstado().then((e) => ({ e, err: null as string | null }))
        .catch((err) => ({ e: null, err: errorDeIA(err) })),
      this._ia.getCatalogo().then((c) => ({ c, err: null as string | null }))
        .catch((err) => ({ c: [] as ModeloCatalogo[], err: errorDeIA(err) })),
    ]);
    this.estado = est.e;
    this.error = est.err;
    this.catalogo = Array.isArray(cat.c) ? cat.c : [];
    this.errorCatalogo = cat.err;
    this.sincronizarAjustes();
    this.cargando = false;
  };

  /** Relee sólo el estado (después de tocar una clave): no repinta toda la solapa. */
  releerEstado = async (): Promise<void> => {
    try {
      this.estado = await this._ia.getEstado();
    } catch { /* el estado anterior sigue en pantalla */ }
  };

  private sincronizarAjustes = (): void => {
    this.modelo = this.estado?.modeloPorOmision || '';
    this.tope = this.estado?.topeDiarioUsd !== undefined && this.estado?.topeDiarioUsd !== null
      ? String(this.estado.topeDiarioUsd) : '';
    this.resultadoAjustes = null;
  };

  get apagada(): boolean { return !!this.estado?.apagado?.apagado; }

  /** Qué proporción del tope de hoy va gastada, de 0 a 1. */
  get fraccion(): number {
    const t = Number(this.estado?.topeDiarioUsd || 0);
    if (!t) return 0;
    return Math.max(0, Math.min(1, Number(this.estado?.gastoHoyUsd || 0) / t));
  }

  get queda(): number {
    return Math.max(0, Number(this.estado?.topeDiarioUsd || 0) - Number(this.estado?.gastoHoyUsd || 0));
  }

  /** El modelo por omisión puede no estar en el catálogo que llegó (o no llegar). */
  get modeloFueraDeCatalogo(): boolean {
    return !!this.modelo && !this.catalogo.some((m) => m.id === this.modelo);
  }

  nombreModelo = (id: string | null | undefined): string => {
    if (!id) return '—';
    const m = this.catalogo.find((x) => x.id === id);
    return m ? m.nombre : id;
  };

  precio = (m: ModeloCatalogo): string =>
    `${usd(m.precioEntrada)} / ${usd(m.precioSalida)} por millón`;

  /** El tope de plataforma es obligatorio: vacío no es «sin tope», es un campo sin llenar. */
  get topeValido(): boolean {
    if (vacio(this.tope)) return false;
    const n = Number(this.tope);
    return !isNaN(n) && n >= 0;
  }

  get ajustesCambiados(): boolean {
    if (!this.estado) return false;
    return this.modelo !== (this.estado.modeloPorOmision || '')
      || Number(this.tope) !== Number(this.estado.topeDiarioUsd);
  }

  guardarAjustes = async (): Promise<void> => {
    if (!this.estado || this.guardandoAjustes || !this.topeValido) return;
    const cambios: { modeloPorOmision?: string; topeDiarioUsd?: number } = {};
    if (this.modelo && this.modelo !== this.estado.modeloPorOmision) cambios.modeloPorOmision = this.modelo;
    if (Number(this.tope) !== Number(this.estado.topeDiarioUsd)) cambios.topeDiarioUsd = Math.round(Number(this.tope) * 100) / 100;
    if (!Object.keys(cambios).length) return;
    this.guardandoAjustes = true;
    this.resultadoAjustes = null;
    try {
      await this._ia.guardarAjustes(cambios);
      await this.releerEstado();
      this.sincronizarAjustes();
      this.resultadoAjustes = { ok: true, texto: 'Guardado. Tarda hasta 10 segundos en tomar efecto.' };
    } catch (e: any) {
      this.resultadoAjustes = { ok: false, texto: errorDeIA(e, true) };
    } finally {
      this.guardandoAjustes = false;
    }
  };

  // ── el interruptor general ──

  abrirApagar = (): void => {
    this.mostrarApagar = true;
    this.causa = 'INCIDENTE';
    this.motivo = '';
    this.palabra = '';
    this.errorApagado = null;
  };

  cerrarApagar = (): void => {
    if (this.cambiandoApagado) return;
    this.mostrarApagar = false;
  };

  get puedeConfirmarApagar(): boolean {
    // Exacta, en mayúsculas: escribirla es la confirmación, no un parecido.
    return this.palabra.trim() === 'APAGAR' && !!this.motivo.trim() && !this.cambiandoApagado;
  }

  apagar = async (): Promise<void> => {
    if (!this.puedeConfirmarApagar) return;
    this.cambiandoApagado = true;
    this.errorApagado = null;
    try {
      await this._ia.cambiarApagado(true, this.causa, this.motivo.trim());
      this.mostrarApagar = false;
      await this.releerEstado();
    } catch (e: any) {
      this.errorApagado = errorDeIA(e, true);
    } finally {
      this.cambiandoApagado = false;
    }
  };

  /** Prender no pide nada: deshace algo que se vuelve a apagar en dos segundos. */
  prender = async (): Promise<void> => {
    if (this.cambiandoApagado) return;
    this.cambiandoApagado = true;
    this.errorApagado = null;
    try {
      await this._ia.cambiarApagado(false);
      await this.releerEstado();
    } catch (e: any) {
      this.errorApagado = errorDeIA(e, true);
    } finally {
      this.cambiandoApagado = false;
    }
  };
}
